import { useState } from "react";
import { toast } from "sonner";
import { Clock, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "ui/card";
import { InputFieldset } from "ui/inputFieldset";
import { Button } from "ui/button";
import { Checkbox } from "ui/checkbox";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";

export interface AtraafSlot {
  id: string;
  slotId?: number;
  name: string;
  startTime: string;
  capacity: number;
  isActive: boolean;
}

const createSlotId = () => {
  if (
    typeof globalThis.crypto?.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  return `slot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};


const emptySlot = { name: "", startTime: "", capacity: "", isActive: true };

export const AtraafSlotsSection = ({
  slots,
  onChange,
}: {
  slots: AtraafSlot[];
  onChange: (slots: AtraafSlot[]) => void;
}) => {
  const [draft, setDraft] = useState(emptySlot);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const addSlot = () => {
    const nextErrors: Record<string, string> = {};
    if (!draft.name.trim()) nextErrors.name = "Name is required.";
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.startTime))
      nextErrors.startTime = "Enter a valid start time in HH:MM format.";
    if (
      !draft.capacity.trim() ||
      !Number.isSafeInteger(Number(draft.capacity)) ||
      Number(draft.capacity) <= 0
    )
      nextErrors.capacity = "Enter a capacity greater than zero.";
    setErrors(nextErrors);
    const firstError = Object.values(nextErrors)[0];
    if (firstError) {
      toast.error(firstError);
      return;
    }
    onChange([
      ...slots,
      {
        id: createSlotId(),
        name: draft.name.trim(),
        startTime: draft.startTime,
        capacity: Number(draft.capacity),
        isActive: draft.isActive,
      },
    ]);
    setDraft(emptySlot);
    toast.success("Slot added to route");
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <div className="h-5 w-1 rounded-full bg-[#5B9BD5]" />
          <h2 className="text-base font-semibold text-gray-900">Atraaf Slot</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-3">
            <InputFieldset
              label="Name"
              aria-label="Slot Name"
              required
              value={draft.name}
              onChange={(e) => {
                setDraft({ ...draft, name: e.target.value });
                setErrors({ ...errors, name: "" });
              }}
              placeholder="e.g. Morning slot"
              aria-invalid={!!errors.name}
              className="bg-white"
            />
          </div>
          <div className="min-w-0 lg:col-span-3">
            <InputFieldset
              label="Start Time"
              aria-label="Slot Start Time"
              required
              type="text"
              value={draft.startTime}
              onChange={(e) => {
                let value = e.target.value.replace(/[^\d:]/g, "");
                if (
                  value.length === 2 &&
                  !value.includes(":") &&
                  value.length > draft.startTime.length
                )
                  value += ":";
                setDraft({ ...draft, startTime: value.slice(0, 5) });
                setErrors({ ...errors, startTime: "" });
              }}
              placeholder="HH:MM"
              maxLength={5}
              aria-invalid={!!errors.startTime}
              className="bg-white"
            />
          </div>
          <div className="min-w-0 lg:col-span-2">
            <InputFieldset
              label="Capacity"
              aria-label="Slot Capacity"
              required
              type="number"
              min={1}
              step={1}
              value={draft.capacity}
              onChange={(e) => {
                setDraft({ ...draft, capacity: e.target.value });
                setErrors({ ...errors, capacity: "" });
              }}
              placeholder="0"
              aria-invalid={!!errors.capacity}
              className="bg-white"
            />
          </div>
          <div className="flex items-center gap-2 lg:col-span-2">
            <Checkbox
              id="slot-is-active"
              checked={draft.isActive}
              onCheckedChange={(checked) =>
                setDraft({ ...draft, isActive: checked === true })
              }
              className="data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5]"
            />
            <Label htmlFor="slot-is-active">Is Active</Label>
          </div>
          <Button
            type="button"
            onClick={addSlot}
            className="self-center bg-[#5B9BD5] text-white sm:col-span-2 lg:col-span-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>
        {slots.length ? (
          <div className="overflow-hidden rounded-lg border border-sky-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#5B9BD5]">
                  <TableHead className="text-white">S.No.</TableHead>
                  <TableHead className="text-white">Name</TableHead>
                  <TableHead className="whitespace-nowrap text-white">
                    Start Time
                  </TableHead>
                  <TableHead className="text-white">Capacity</TableHead>
                  <TableHead className="text-white">Is Active</TableHead>
                  <TableHead className="text-right text-white">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slots.map((slot, index) => (
                  <TableRow key={slot.id}>
                    <TableCell className="text-sm font-medium py-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#5B9BD5] to-[#4A7FB0] flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                    </TableCell>
                    <TableCell>{slot.name}</TableCell>
                    <TableCell>{slot.startTime}</TableCell>
                    <TableCell>{slot.capacity}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          slot.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }
                      >
                        {slot.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete slot ${slot.name}`}
                        onClick={() =>
                          onChange(slots.filter((item) => item.id !== slot.id))
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-gray-500">
            <Clock className="mx-auto mb-1 h-6 w-6 text-gray-300" />
            <p className="text-sm font-medium">No slots added yet</p>
            <p className="mt-1 text-xs">Add slots for this atraaf.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
